import { ArrowRight, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { DetailPane, DetailSection, KeyValue, detailActionClass } from '@/components/data/DetailPane'
import { api } from '@/lib/api'
import { durationMs, fmtDuration, statusTone } from '@/lib/audit'

/** Secondary button in audit page headers. */
export const auditButtonClass =
  'inline-flex h-7 items-center gap-1.5 rounded-md border border-line-strong bg-surface-raised px-2.5 text-[12px] text-foreground transition-colors hover:bg-surface-active disabled:opacity-60'

export type AuditDetail = {
  run_id: string
  actor_email: string
  surface: string
  operation: string
  status: string
  dry_run: boolean
  critical: boolean
  started_at: string
  finished_at: string | null
  parameters: Record<string, unknown>
  changes: unknown[] | null
  findings: unknown[] | null
  warnings: string[] | null
  atlassian_summary: Record<string, unknown> | null
  error: string | null
  atlassian_silent_failure: boolean
}

/** One run from /audit/{id}: undefined — loading, null — not found / failed. */
export function useAuditRun(runId: string | null | undefined): AuditDetail | null | undefined {
  const [d, setD] = useState<{ id: string; run: AuditDetail | null } | null>(null)
  useEffect(() => {
    if (!runId) return
    let live = true
    api<AuditDetail>(`/audit/${encodeURIComponent(runId)}`)
      .then((r) => { if (live) setD({ id: runId, run: r }) })
      .catch(() => { if (live) setD({ id: runId, run: null }) })
    return () => { live = false }
  }, [runId])
  if (!runId) return null
  return d && d.id === runId ? d.run : undefined
}

/** Re-run: the operation page opens with the form pre-filled from the run's parameters. */
export function useRerun() {
  const navigate = useNavigate()
  return (d: AuditDetail) => navigate(`/operations/${d.operation}`, { state: { prefill: d.parameters } })
}

export function StatusLine({ d }: { d: AuditDetail }) {
  return (
    <span className="flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
      <span className={`font-medium ${statusTone(d.status)}`}>{d.status}</span>
      <span className="font-mono text-[12px] text-text-2">{fmtDuration(durationMs(d.started_at, d.finished_at))}</span>
      {d.dry_run && <span className="text-text-2">dry run</span>}
      {d.critical && <span className="font-medium text-danger">critical</span>}
      {d.atlassian_silent_failure && <span className="font-medium text-warn">silent failure</span>}
    </span>
  )
}

export function runFacts(d: AuditDetail): [string, React.ReactNode][] {
  return [
    ['Who', <>{d.actor_email} <span className="text-text-3">· {d.surface}</span></>],
    ['Started', <span className="font-mono text-[12px]">{new Date(d.started_at).toLocaleString()}</span>],
    ['Finished', <span className="font-mono text-[12px]">{d.finished_at ? new Date(d.finished_at).toLocaleString() : '—'}</span>],
    ['Run', <span className="font-mono text-[12px] text-text-2">{d.run_id}</span>],
  ]
}

export function CodeBlock({ children, tone = 'text-text-2' }: { children: React.ReactNode; tone?: string }) {
  return (
    <pre className={`overflow-auto whitespace-pre-wrap break-words rounded-md bg-surface p-2.5 font-mono text-[11.5px] leading-relaxed ${tone}`}>
      {children}
    </pre>
  )
}

/** Detail pane of a run in the audit log. */
export function RunSummary({ d }: { d: AuditDetail }) {
  const rerun = useRerun()
  const params = Object.keys(d.parameters ?? {}).length ? JSON.stringify(d.parameters, null, 2) : null
  return (
    <DetailPane
      eyebrow="Run"
      title={<span className="font-mono">{d.operation}</span>}
      status={<StatusLine d={d} />}
      actions={(
        <>
          <button type="button" onClick={() => rerun(d)} className={detailActionClass}>
            <RotateCcw aria-hidden className="size-3.5" /> Re-run
          </button>
          <Link to={`/audit/${d.run_id}`} className={detailActionClass}>
            Open run <ArrowRight aria-hidden className="size-3.5" />
          </Link>
        </>
      )}
    >
      <KeyValue items={runFacts(d)} />
      {d.error && (
        <DetailSection title="Error">
          <CodeBlock tone="text-danger">{d.error}</CodeBlock>
        </DetailSection>
      )}
      {d.warnings && d.warnings.length > 0 && (
        <DetailSection title={`Warnings · ${d.warnings.length}`}>
          <ul className="space-y-1 text-warn">
            {d.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </DetailSection>
      )}
      {params && (
        <DetailSection title="Parameters">
          <CodeBlock>{params}</CodeBlock>
        </DetailSection>
      )}
      <p className="text-[12px] text-text-3">
        {d.changes?.length ?? 0} changes · {d.findings?.length ?? 0} findings — full lists on the run page.
      </p>
    </DetailPane>
  )
}
